import { useTranslation } from 'react-i18next';
import { tokens, Button, Text } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { useCallback } from 'react';

import PENDING_IMAGE from '@hotjar/shared-assets/images/feedback/inactive-state.svg';
import { useToggleSurveyStatusMutation } from '@hotjar/feedback/domain/Surveys/query';
import type { Site } from '@hotjar/site';
import type { Survey } from '@hotjar/feedback/domain/Surveys/Survey/Survey';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: ${tokens.spacer.x8pt5} auto;
  max-width: 351px;
  text-align: center;
`;

const StyledTitle = styled(Text)`
  margin-top: 24px;
`;

const ActivateButton = styled(Button)`
  margin-top: 14px;
`;

const Subtitle = styled(Text)`
  margin-top: 9px;
`;

export interface InactiveStateProps {
  site: Site;
  survey: Survey;
}

export const InactiveState = ({ site, survey }: InactiveStateProps): JSX.Element => {
  const toggleSurveyStatusMutation = useToggleSurveyStatusMutation();
  const { t } = useTranslation('feedback');

  const onActivate = useCallback(() => {
    if (!survey.id || !site.id) return;
    toggleSurveyStatusMutation.mutateAsync({ siteId: site.id, surveyId: survey.id });
  }, [survey.id, site.id, toggleSurveyStatusMutation]);

  return (
    <Container>
      <img src={PENDING_IMAGE} alt="Survey not yet active" />
      <StyledTitle as="h5" textType="header5">
        {t('inactive-states.title')}
      </StyledTitle>
      <Subtitle as="p" textType="body1" textColor="text_light">
        {t('inactive-states.subtitle', { surveyName: survey.name })}
      </Subtitle>

      <ActivateButton
        isLoading={toggleSurveyStatusMutation.isLoading}
        kind="primary"
        size="m"
        onClick={onActivate}
      >
        {t('inactive-states.cta')}
      </ActivateButton>
    </Container>
  );
};
